import { type UseQueryOptions, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { ApiError, getApiDevosSeriesProgress, getGetApiDevosSeriesProgressQueryOptions } from "twi-client";

import { queryClient } from "~/entry.client";
import { usePlayerStore } from "~/hooks/usePlayerStore";
import { Sentry } from "~/lib/sentry";
import type { ProgressByEpisodeItem } from "~/lib/series.utils";
import { normalizeSeriesProgressResponse, type SeriesProgressResponse } from "~/services/progress.client";

export const SERIES_PROGRESS_QUERY_KEY = {
  all: ["series-progress"],
  bySeriesId: (seriesId: string) => ["series-progress", seriesId],
};

export function getSeriesProgressQueryOptions(seriesId: string) {
  const queryKey = getGetApiDevosSeriesProgressQueryOptions(
    { seriesId },
    {
      query: {
        queryKey: SERIES_PROGRESS_QUERY_KEY.bySeriesId(seriesId),
      },
    },
  ).queryKey;

  return {
    queryKey,
    queryFn: async ({ signal }) => {
      try {
        const data = await getApiDevosSeriesProgress({ seriesId }, signal);
        return normalizeSeriesProgressResponse(data, seriesId);
      } catch (error) {
        // TODO: Stop swallowing errors in this query function and throw instead.
        // TODO: Update callsites to interpret error states as null and handle them explicitly.
        if (error instanceof ApiError) {
          return null;
        }

        Sentry.captureException(error, { tags: { context: "getSeriesProgress" } });
        return null;
      }
    },
  } satisfies UseQueryOptions<SeriesProgressResponse | null, Error>;
}

export function ensureSeriesProgressData(seriesId: string) {
  return queryClient.ensureQueryData(getSeriesProgressQueryOptions(seriesId));
}

export function ensureSeriesProgressBatch(seriesIds: Array<string>) {
  const uniqueSeriesIds = [...new Set(seriesIds.filter(Boolean))];
  return Promise.all(uniqueSeriesIds.map((seriesId) => ensureSeriesProgressData(seriesId)));
}

/**
 * Hook to fetch and manage series progress data
 * Combines data from the API and local player store
 */
export function useSeriesProgress(seriesId: string | undefined, enabled: boolean) {
  const progressData = usePlayerStore((state) => state.progressData);
  const queryOptions: UseQueryOptions<SeriesProgressResponse | null, Error> = seriesId
    ? {
        ...getSeriesProgressQueryOptions(seriesId),
        enabled,
      }
    : {
        queryKey: SERIES_PROGRESS_QUERY_KEY.bySeriesId(""),
        enabled: false,
        queryFn: async () => null,
      };

  const { data: seriesProgress } = useQuery(queryOptions);

  const progressByEpisode = useMemo(() => {
    const map = new Map<string, ProgressByEpisodeItem>();

    // Add progress from API response
    if (seriesProgress?.devotionals) {
      for (const devoProgress of seriesProgress.devotionals) {
        map.set(devoProgress.cmsId, {
          progressPercentage: devoProgress.progressPercentage,
          lastListenedUtc: devoProgress.lastListenedUtc,
          completedAtUtc: devoProgress.completedAtUtc,
          cmsId: devoProgress.cmsId,
        });
      }
    }

    // Add progress from local store (fallback)
    if (Array.isArray(progressData)) {
      for (const item of progressData) {
        if (!map.has(item.cmsId)) {
          map.set(item.cmsId, {
            progressPercentage: item.progressPercentage,
            lastListenedUtc: item.lastListenedUtc,
            completedAtUtc: item.completedAtUtc,
            cmsId: item.cmsId,
          });
        }
      }
    }

    return map;
  }, [seriesProgress?.devotionals, progressData]);

  return {
    seriesProgress,
    progressByEpisode,
  };
}

/**
 * Optimistically updates all series-progress queries with current progress
 * @param cmsId - The CMS ID of the episode
 * @param progressPercentage - The current progress percentage
 * @param completedAtUtc - The UTC timestamp when the episode was completed (only when 100%)
 * @param lastListenedUtc - The UTC timestamp when the episode was last listened to
 */
export function optimisticallyUpdateSeriesProgressQueries(
  cmsId: string,
  progressPercentage: number,
  completedAtUtc?: string,
  lastListenedUtc?: string,
): void {
  // Optimistically update all series-progress queries
  queryClient.setQueriesData<SeriesProgressResponse | null>({ queryKey: SERIES_PROGRESS_QUERY_KEY.all }, (oldData) => {
    if (!oldData) return oldData;

    const updatedDevotionals = oldData.devotionals.map((devo) => {
      if (devo.cmsId === cmsId) {
        return { ...devo, progressPercentage, completedAtUtc, lastListenedUtc };
      }
      return devo;
    });

    return {
      ...oldData,
      devotionals: updatedDevotionals,
    };
  });
}
